import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Mcf } from '../../../models/api/core/list/Mcf';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { ClaimHeaderConfig } from '../../../models/api/view/claim/header/claim-header-config';
import { ClaimHeaderField } from '../../../models/api/view/claim/header/claim-header-field';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import {
  PickListComponent,
  PickListGroup,
} from '../../core/controls/pick-list.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { HeaderField } from './claim-header.component';

@Component({
  selector: 'app-claim-header-dialog',
  imports: [
    RouterModule,
    ActionRowComponent,
    ButtonComponent,
    CommonModule,
    DialogComponent,
    RadioButtonComponent,
    PickListComponent,
  ],
  template: `
    @if (dialogMode) {
      <app-dialog #dialog [label]="label" width="55rem" (show)="dialogShow()">
        <ng-template #content>
          <ng-container *ngTemplateOutlet="content2"> </ng-container>
        </ng-template>
      </app-dialog>
    } @else {
      <ng-container *ngTemplateOutlet="content2"> </ng-container>
    }

    <ng-template #content2>
      @if (isInitialized) {
        <div>
          @if (canShowClient) {
            <div class="tw-pb-1 tw-pl-4">
              <app-radio-button
                [control]="showClient"
                label="Options"
                optionLabel="label"
                optionValue="value"
                [options]="showClientOptions"
              />
            </div>
          }
          <app-pick-list
            [sourceControl]="availableControl"
            [targetControl]="selectedControl"
            labelName="label"
            keyName="name"
            helpText="10 maximum"
            [inDialog]="true"
          />
          <div class="tw-pt-1">
            <app-action-row>
              <app-button
                left1
                emphasis="Low"
                (click)="restoreDefault()"
                label="Restore defaults"
              />
              <app-button
                right1
                emphasis="High"
                [disabled]="
                  selectedControl.value?.length === 0 ||
                  selectedControl.value?.length > 10
                "
                (click)="saveCustomizedView()"
                label="Save"
              />
              <app-button
                right2
                emphasis="Low"
                (click)="onCancel()"
                label="Cancel"
              />
            </app-action-row>
          </div>
        </div>
      } @else {
        <div class="tw-italics tw-min-h-[280px]">loading...</div>
      }
    </ng-template>
  `,
  styles: ``,
})
export class ClaimHeaderDialogComponent extends BaseComponent {
  private snackbar = inject(Snackbar);

  @ViewChild('dialog')
  dialog: DialogComponent;

  @Input()
  label: string;

  @Input()
  dialogMode = true;

  @Input()
  headerFields: HeaderField[] = [];

  @Output()
  headerFieldsChange = new EventEmitter<string[]>();

  mcfs: Mcf[];

  showClientOptions: any[] = [];

  @Input()
  set visible(value: boolean) {
    this.dialog.visible = true;
  }
  get visible(): boolean {
    return this.dialog.visible;
  }

  showClient = new FormControl<boolean>(false);

  availableControl = new FormControl<PickListGroup[]>([]);

  selectedControl = new FormControl<any[]>(
    [],
    [Validators.required, Validators.maxLength(10), Validators.minLength(1)],
  );

  claim: ClaimRow;

  canShowClient = true;

  isInitialized = false;

  private initialSelectedFields: DialogHeaderField[] = [];

  // Stores misc fields temporarily when switching to LOB mode so they can be restored
  private hiddenMiscFields: DialogHeaderField[] = [];

  // Map header field names to restrictedFields keys (WEB_RESTRICT suppression)
  private readonly headerToRestricted: Record<string, string> = {
    dateCreate: 'DateCreate',
    dateOpen: 'DateOpened',
    dateClosed: 'DateClosed',
    subType: 'SubType',
    dateRptByClient: 'DateRptByClient',
    dateReptToTPA: 'DateReptToTPA',
  };

  /**
   * Checks if a field is restricted based on user's field security settings.
   */
  private isFieldRestricted(fieldName: string): boolean {
    const restrictedFields =
      this.session.user?.fieldSecurity?.restrictedFields ?? [];
    const restrictedName = this.headerToRestricted[fieldName];
    return restrictedName != null && restrictedFields.includes(restrictedName);
  }

  async dialogShow() {
    this.isInitialized = false;

    this.claim = this.view.currentClaim();

    const lists = await this.api.list.get();
    const lob = lists.linesOfBusiness.find(
      (x) => x.value === this.claim.lineOfBusiness,
    );

    const lobDesc =
      this.claim.lineOfBusiness === 'DS' && this.claim.coverageCode === 'AC'
        ? 'accommodation'
        : lob.description.toLowerCase();

    this.label = `Configure ${lobDesc} header`;
    const clientFromList = lists.clients?.find(
      (c) => c.value === this.claim?.contractNumber,
    );
    const clientDisplay =
      this.claim?.client ??
      this.claim?.clientName ??
      clientFromList?.description ??
      this.claim?.contractNumber ??
      'this client';
    this.showClientOptions = [
      { label: `Configure for all ${lobDesc} claims`, value: false },
      { label: `Configure for ${clientDisplay} claims`, value: true },
    ];

    this.canShowClient =
      ['DS', 'LA', 'AD'].indexOf(lob.value) === -1 &&
      this.session.authorize('Miscellaneous') &&
      !this.session.isSpecificClaimUser;

    this.dispose.subs = this.showClient.valueChanges.subscribe(() =>
      this.loadLists(true),
    );
    await this.loadLists();
    // Store initial selected fields for cancel functionality
    if (this.selectedControl.value) {
      this.initialSelectedFields = [...this.selectedControl.value];
    }
    this.isInitialized = true;
  }

  async loadLists(preserveSelection = false) {
    // Save current selection if we need to preserve it
    const currentSelection = preserveSelection
      ? this.selectedControl.value
      : null;

    // First, get selected configs to determine if we should default to client-specific mode
    const selected = await this.api.claimHeaderDialog.getSelected();

    const contractNumber = Number.parseInt(this.claim.contractNumber);
    const selectedByContract = selected.find(
      (x) =>
        x.contractNumber === contractNumber &&
        x.lineOfBusiness === this.claim.lineOfBusiness,
    );
    const selectedByLob = selected.find(
      (x) =>
        x.lineOfBusiness === this.claim.lineOfBusiness && !x.contractNumber,
    );

    if (selectedByContract && !this.isInitialized) {
      this.showClient.setValue(true, { emitEvent: false });
    }

    // load all available items
    const groups: PickListGroup[] = [];

    const defaultConfigs = (
      await this.api.claimHeaderDialog.getAvailable()
    ).filter((x) => x.lineOfBusiness === this.claim.lineOfBusiness);

    const standardConfig = defaultConfigs.find(
      (x) =>
        !x.contractNumber ||
        x.contractNumber === null ||
        x.contractNumber === 0,
    );

    const clientConfig = defaultConfigs.find(
      (x) => x.contractNumber === Number.parseInt(this.claim.contractNumber),
    );

    const availableStandardFields = clientConfig
      ? clientConfig
      : standardConfig;

    // Filter out restricted fields from available fields
    const filteredStandardFields =
      availableStandardFields?.items?.filter(
        (field) => !this.isFieldRestricted(field.name),
      ) || [];

    groups.push(
      new PickListGroup(
        'Standard fields',
        filteredStandardFields.map(
          (field) => new DialogHeaderField(field.name, field.label, field.mcf),
        ),
      ),
    );

    // Only load Client fields (MCF) if user has security permission
    if (
      this.showClient.value &&
      this.claim &&
      this.session.authorize('Miscellaneous') &&
      !this.session.isSpecificClaimUser
    ) {
      this.mcfs = await this.api.list.getMcfs(
        Number.parseInt(this.claim.contractNumber),
      );
      groups.push(
        new PickListGroup(
          'Client fields',
          this.mcfs.map(
            (x) =>
              new DialogHeaderField(x.fieldNumber.toString(), x.fieldLabel, x),
          ),
        ),
      );
    }

    this.availableControl.setValue(groups);

    // If preserving selection, restore the current selection (filtering out misc fields if switching to LOB mode)
    if (preserveSelection && currentSelection) {
      let restoredSelection = currentSelection;

      if (!this.showClient.value) {
        // Switching to LOB mode - save misc fields and filter them out
        const miscFields = currentSelection.filter((field: DialogHeaderField) =>
          /^\d+$/.test(field.name),
        );
        // Store the misc fields so they can be restored later
        this.hiddenMiscFields = miscFields;

        restoredSelection = currentSelection.filter(
          (field: DialogHeaderField) => !/^\d+$/.test(field.name),
        );
      } else {
        // Switching to Client mode - restore any hidden misc fields
        if (this.hiddenMiscFields.length > 0) {
          restoredSelection = [...currentSelection, ...this.hiddenMiscFields];
          this.hiddenMiscFields = [];
        }
      }

      this.selectedControl.setValue(restoredSelection);
    } else {
      // Prioritize client-specific config over LOB-only config
      const selectedFields = selectedByContract
        ? selectedByContract
        : selectedByLob;

      // Populate selectedControl with the selected fields
      this.selectedControl.setValue(
        selectedFields?.items?.map(
          (field) => new DialogHeaderField(field.name, field.label, field.mcf),
        ),
      );
    }
  }

  async restoreDefault() {
    const defaultClientConfigs = (
      await this.api.claimHeaderDialog.getDefault()
    ).find((x) => x.lineOfBusiness === this.claim.lineOfBusiness);

    // Filter out restricted fields from defaults
    const filteredDefaults =
      defaultClientConfigs?.items?.filter(
        (field) => !this.isFieldRestricted(field.name),
      ) || [];

    this.selectedControl.setValue(
      filteredDefaults.map(
        (field) => new DialogHeaderField(field.name, field.label, field.mcf),
      ),
    );
  }

  onCancel() {
    // Restore initial selected fields and reset hidden misc fields
    this.selectedControl.setValue([...this.initialSelectedFields], {
      emitEvent: false,
    });
    this.hiddenMiscFields = [];
    this.dialog.visible = false;
  }

  private updateHeaderFields() {
    if (this.selectedControl.value) {
      const selectedFieldNames = this.selectedControl.value.map(
        (dialogField) => dialogField.name,
      );
      this.headerFieldsChange.emit(selectedFieldNames);
    }
  }

  async saveCustomizedView() {
    if (this.selectedControl.valid) {
      // Get values from the selectedControl and convert to ClaimHeaderField[]
      const selectedFields: ClaimHeaderField[] = this.selectedControl.value.map(
        (x) => ({
          name: x.name,
          label: x.label,
          mcf: x.data,
        }),
      );

      // Check if any selected fields are Misc Fields (numeric field names)
      // If Misc Fields are included, force save as client-specific
      const hasMiscFields = selectedFields.some((field) =>
        /^\d+$/.test(field.name),
      );
      const isClientSpecific = this.showClient.value || hasMiscFields;

      const claimConfig: ClaimHeaderConfig = {
        contractNumber: isClientSpecific
          ? Number.parseInt(this.claim.contractNumber)
          : null,
        lineOfBusiness: this.claim.lineOfBusiness,
        items: selectedFields,
      };
      const saveSettings: ClaimHeaderConfig[] = [claimConfig];

      const currentSettings = await this.api.claimHeaderDialog.getSelected();
      if (isClientSpecific) {
        // Remove existing client-specific config for this contract + LOB
        const index = currentSettings.findIndex(
          (x) =>
            x.contractNumber === claimConfig.contractNumber &&
            x.lineOfBusiness === claimConfig.lineOfBusiness,
        );
        if (index > -1) {
          currentSettings.splice(index, 1);
        }
      } else {
        // Remove existing LOB-only config (no contract number)
        const index = currentSettings.findIndex(
          (x) =>
            x.lineOfBusiness === claimConfig.lineOfBusiness &&
            !x.contractNumber,
        );
        if (index > -1) {
          currentSettings.splice(index, 1);
        }

        for (let i = currentSettings.length - 1; i >= 0; i--) {
          if (
            currentSettings[i].lineOfBusiness === claimConfig.lineOfBusiness &&
            currentSettings[i].contractNumber
          ) {
            currentSettings.splice(i, 1);
          }
        }
      }
      saveSettings.push(...currentSettings);
      try {
        await this.api.claimHeaderDialog.saveSelected(saveSettings);

        // Refresh cache
        await this.api.claimHeaderDialog.getSelected(true);

        // Update headerFields with selectedControl values after successful save
        this.updateHeaderFields();
        // Update stored initial state after successful save
        if (this.selectedControl.value) {
          this.initialSelectedFields = [...this.selectedControl.value];
        }
        // Reset hidden misc fields after save
        this.hiddenMiscFields = [];
        this.dialog.visible = false;
        this.snackbar.show('Header configuration saved', 'Success');
      } catch (error) {
        this.snackbar.show('Error saving header configuration', 'Error');
      }
    }
  }
}

class DialogHeaderField {
  constructor(
    public name: string,
    public label: string,
    public data: any = null,
  ) {}
}
